import type { AddressVerificationRequest, AddressVerificationResponse } from "@credit-acceptance/step6-bff-client";

import { ADDRESS_STREET_NAME_MAX_LENGTH, ADDRESS_STREET_NUMBER_MAX_LENGTH } from "./constants";
import type { CustomerInfoInput } from "./Signers.schema";

// ---------------------------------------------------------------------------
// addressVerification
//
// Pure logic for the Address Standardization feature (docs/address-
// standardization-spec.md). No React — unit-testable in isolation, consumed
// by useAddressVerificationGate.ts.
// ---------------------------------------------------------------------------

export type PersonPath = "driver" | "secondSigner";
export type AddressKind = "physical" | "mailing";

/** One of the 4 verified address slots (spec §2). */
export interface AddressSlot {
  personPath: PersonPath;
  kind: AddressKind;
}

export const ADDRESS_SLOTS: readonly AddressSlot[] = [
  { personPath: "driver", kind: "physical" },
  { personPath: "driver", kind: "mailing" },
  { personPath: "secondSigner", kind: "physical" },
  { personPath: "secondSigner", kind: "mailing" },
];

/**
 * Builds the slot's stable bookkeeping key (not a saved field — session-local gate state).
 * @param {AddressSlot} slot - The address slot.
 * @returns {string} The slot's stable key.
 */
export function addressSlotKey(slot: AddressSlot): string {
  return `${slot.personPath}.${slot.kind}`;
}

/** The 6 user-editable address fields for one slot, independent of the `physical`/`mailing` prefix. */
export interface AddressSnapshotFields {
  streetNumber: string;
  streetName: string;
  apartmentNumber: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * Reads a slot's current address fields off the form's customer info.
 * @param {CustomerInfoInput} customerInfo - The person's customer info.
 * @param {AddressKind} kind - Which address (physical/mailing) to read.
 * @returns {AddressSnapshotFields} That address's 6 fields.
 */
export function readSlotFields(customerInfo: CustomerInfoInput, kind: AddressKind): AddressSnapshotFields {
  return kind === "physical"
    ? {
        streetNumber: customerInfo.physicalStreetNumber,
        streetName: customerInfo.physicalStreetName,
        apartmentNumber: customerInfo.physicalApartmentNumber,
        city: customerInfo.physicalCity,
        state: customerInfo.physicalState,
        zip: customerInfo.physicalZip,
      }
    : {
        streetNumber: customerInfo.mailingStreetNumber,
        streetName: customerInfo.mailingStreetName,
        apartmentNumber: customerInfo.mailingApartmentNumber,
        city: customerInfo.mailingCity,
        state: customerInfo.mailingState,
        zip: customerInfo.mailingZip,
      };
}

/**
 * Concatenates a slot's address fields into the case-insensitive comparison
 * string used to detect a change since the last verification (spec §3):
 * `streetNumber + streetName + apartmentNumber + city + state + zip`.
 * @param {AddressSnapshotFields} fields - The address fields to concatenate.
 * @returns {string} The lowercased, trimmed comparison string.
 */
export function concatAddress(fields: AddressSnapshotFields): string {
  return `${fields.streetNumber}${fields.streetName}${fields.apartmentNumber}${fields.city}${fields.state}${fields.zip}`
    .trim()
    .toLowerCase();
}

/**
 * Whether a slot's address has changed since the last successful verification
 * (spec §3). `lastVerifiedConcat` is `undefined` before any baseline has been
 * captured for this session (e.g. this app has no persisted per-field
 * "last verified" snapshot from the backend yet, so the baseline is seeded
 * from the hydrated address at form-mount — see useAddressVerificationGate).
 * Blank-to-non-blank counts as changed; blank-to-blank does not.
 * @param {string | undefined} lastVerifiedConcat - The slot's baseline concat (see concatAddress), or undefined if none yet.
 * @param {AddressSnapshotFields} fields - The slot's current address fields.
 * @returns {boolean} True when the address differs from the baseline.
 */
export function hasAddressChanged(lastVerifiedConcat: string | undefined, fields: AddressSnapshotFields): boolean {
  return concatAddress(fields) !== (lastVerifiedConcat ?? "");
}

/**
 * Pre-flight field validation (spec §4 / §6): the 5 fields USPS requires
 * before a verify call is worth making. A slot failing this is "changed but
 * unverifiable" — skip the API call and let Save proceed normally.
 * @param {AddressSnapshotFields} fields - The address fields to validate.
 * @returns {boolean} True when every required field is present and well-formed.
 */
export function isAddressValidForVerification(fields: AddressSnapshotFields): boolean {
  return (
    fields.streetNumber.trim() !== "" &&
    fields.streetNumber.length <= ADDRESS_STREET_NUMBER_MAX_LENGTH &&
    fields.streetName.trim() !== "" &&
    fields.streetName.length <= ADDRESS_STREET_NAME_MAX_LENGTH &&
    fields.city.trim() !== "" &&
    fields.state.trim().length === 2 &&
    isValidZipForVerification(fields.zip)
  );
}

/**
 * ZIP must be 5–10 characters (plain 5-digit or ZIP+4 "#####-####") and non-blank.
 * @param {string} zip - The raw ZIP field value.
 * @returns {boolean} True when the ZIP's length is within range.
 */
function isValidZipForVerification(zip: string): boolean {
  const trimmed = zip.trim();
  return trimmed.length >= 5 && trimmed.length <= 10;
}

/**
 * Maps a slot's form fields onto the `/addresses/verify` request body (spec §5).
 * @param {AddressSnapshotFields} fields - The address fields to send.
 * @returns {AddressVerificationRequest} The verify request body.
 */
export function buildAddressVerificationRequest(fields: AddressSnapshotFields): AddressVerificationRequest {
  return {
    streetNumber: fields.streetNumber,
    streetName: fields.streetName,
    suite: fields.apartmentNumber.trim() === "" ? undefined : fields.apartmentNumber,
    city: fields.city,
    state: fields.state,
    postCode: fields.zip,
  };
}

/**
 * Builds the standardized address fields from a verify response, falling back
 * to the entered value for any field the response left blank. `streetType` is
 * appended to `streetName` for display, matching legacy
 * `checkAddrStdnResponseFields` (spec §5). `postCode5` is preferred over
 * `postCode` when both are present (spec §5).
 * @param {AddressVerificationResponse} response - The verify response.
 * @param {AddressSnapshotFields} entered - The address as entered, used as a fallback for blank response fields.
 * @returns {AddressSnapshotFields} The standardized address fields.
 */
export function buildStandardizedAddressFields(
  response: AddressVerificationResponse,
  entered: AddressSnapshotFields
): AddressSnapshotFields {
  const streetName = [response.streetName, response.streetType].filter((part) => (part ?? "").trim() !== "").join(" ");
  return {
    streetNumber: response.streetNumber || entered.streetNumber,
    streetName: streetName || entered.streetName,
    apartmentNumber: response.suite ?? entered.apartmentNumber,
    city: response.city || entered.city,
    state: response.state || entered.state,
    zip: response.postCode5 || response.postCode || entered.zip,
  };
}

// ---------------------------------------------------------------------------
// Status enum + normalization (spec §5's "Status enum" — case-insensitive,
// resilient to casing differences between environments; an unrecognized value
// is treated as ErrorBadCall, matching "our request was wrong" semantics
// rather than silently fail-opening on an unknown status).
// ---------------------------------------------------------------------------
export type VerificationStatus =
  | "Ok"
  | "WarningChanged"
  | "ErrorSubpremise"
  | "ErrorFailed"
  | "ErrorServiceFailed"
  | "ErrorBadCall";

const STATUS_BY_LOWERCASE: Record<string, VerificationStatus> = {
  ok: "Ok",
  warningchanged: "WarningChanged",
  errorsubpremise: "ErrorSubpremise",
  errorfailed: "ErrorFailed",
  errorservicefailed: "ErrorServiceFailed",
  errorbadcall: "ErrorBadCall",
};

/**
 * Case-insensitively maps a raw verify-response status to the known enum, falling back to ErrorBadCall for anything unrecognized.
 * @param {string | undefined} raw - The verify response's `status` field.
 * @returns {VerificationStatus} The normalized status.
 */
export function normalizeVerificationStatus(raw: string | undefined): VerificationStatus {
  if (!raw) {
    return "ErrorBadCall";
  }
  return STATUS_BY_LOWERCASE[raw.toLowerCase()] ?? "ErrorBadCall";
}

/** Statuses that open the AddressVerificationModal (spec §7). */
export type BlockingVerificationStatus = "WarningChanged" | "ErrorSubpremise" | "ErrorFailed";

/**
 * Whether a status is one of the 3 that open the AddressVerificationModal.
 * @param {VerificationStatus} status - The normalized verification status.
 * @returns {boolean} True for WarningChanged/ErrorSubpremise/ErrorFailed.
 */
export function isBlockingStatus(status: VerificationStatus): status is BlockingVerificationStatus {
  return status === "WarningChanged" || status === "ErrorSubpremise" || status === "ErrorFailed";
}

/** Retry cap per slot per Save attempt (spec §9) — on the 3rd non-Ok result, fall through to the ErrorFailed treatment. */
export const ADDRESS_VERIFICATION_RETRY_CAP = 3;
